import {
  getLegacyColumnId,
  migrateLegacyColumnId,
} from "@/pages/employees/index/employee-column-keys.ts";
import { useEmployeeCatalogColumns } from "@/pages/employees/index/use-employee-catalog-columns.tsx";
import {
  ENTITY_ORDER,
  useEmployeeFilterCategories,
} from "@/pages/employees/index/use-employee-filter-categories.ts";
import { useEmployeeColumns } from "@/pages/employees/index/employees-columns.tsx";
import { ApiColumnConfigType, ColumnConfig, EmployeeColumnId } from "@/types/column.types.ts";
import {
  ApiEmployeeColumn,
  EmployeeColumnEntity,
  parseCustomFieldKey,
  parseCustomTableKey,
} from "@/types/employees/employee-column.types.ts";
import { ApiEmployee } from "@/types/employees/employee.types.ts";
import { ColumnDef } from "@tanstack/react-table";
import { omit } from "lodash";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

// Дефолт списка в ключах каталога — та же тройка, что была на legacy-id
export const DEFAULT_CATALOG_COLUMNS = [
  "employee.position.job_profile",
  "employee.position.department",
  "employee.position.manager",
];

/**
 * Сортировка идёт на бэке (`manualSorting`), но TanStack Table включает её
 * только при наличии accessor'а: `getCanSort()` проверяет `!!column.accessorFn`.
 * Значение accessor'а никуда не идёт — строки не сортируются на клиенте, а поле
 * запроса Table берёт из `column.id`.
 */
const sortableAccessor = (column: ApiEmployeeColumn) =>
  column.is_sortable
    ? { accessorFn: () => column.id, enableSorting: true, sortDescFirst: false }
    : { enableSorting: false };

/**
 * Колонки списка сотрудников по каталогу с бэка.
 *
 * Каталог даёт состав, заголовки и метаданные (`value_type`, `filter_type`,
 * сортируемость), а рендер ячейки берётся из существующих определений:
 * системные колонки и кастомные поля профиля уже учитывают права, приватность
 * даты рождения и валюту. Своими рендерами закрываются только те колонки,
 * которых в списке раньше не было — столбцы кастомных таблиц и доп. компенсации.
 */
export const useEmployeeColumnsCatalog = () => {
  const { t } = useTranslation();
  const {
    catalogColumns,
    columnByKey,
    columnsByEntity,
    entityTitles,
    tableTitleById,
    filterCategories,
    isLoading,
    isError,
  } = useEmployeeFilterCategories();
  const { columnDefinitions: legacyDefinitions } = useEmployeeColumns();
  const buildCatalogColumn = useEmployeeCatalogColumns();

  /**
   * Определения по ключам каталога. Legacy-определение переиспользуется с
   * заголовком из каталога: заголовки колонок общие с People Analytics и
   * приходят уже переведёнными.
   */
  const columnDefinitions = useMemo<Record<string, ColumnDef<ApiEmployee>>>(() => {
    const definitions: Record<string, ColumnDef<ApiEmployee>> = {
      [EmployeeColumnId.employee_name]: legacyDefinitions[EmployeeColumnId.employee_name],
    };

    catalogColumns.forEach((column) => {
      const legacyId = getLegacyColumnId(column.id);
      const customField = parseCustomFieldKey(column.id);
      const legacyKey = legacyId ?? (customField?.isCurrency ? undefined : customField?.slug);
      const definition =
        (legacyKey ? legacyDefinitions[legacyKey] : undefined) ?? buildCatalogColumn(column);

      if (definition) {
        definitions[column.id] = {
          // `accessorKey` legacy-определения указывает на поле, которого в
          // ответе нет: значение достаёт `cell`, а accessor нужен только чтобы
          // TanStack Table разрешил сортировку (`getCanSort` требует accessorFn)
          ...omit(definition, "accessorKey"),
          ...sortableAccessor(column),
          id: column.id,
          header: column.title,
        };
      }
    });

    return definitions;
  }, [catalogColumns, legacyDefinitions, buildCatalogColumn]);

  /** Конфиг для модалки настройки колонок: группировка по сущности из ключа. */
  const columnConfig = useMemo<ColumnConfig>(() => {
    const categories = ENTITY_ORDER.flatMap((entity) => {
      const columns = (columnsByEntity[entity] ?? []).filter(
        (column) => columnDefinitions[column.id]
      );
      if (!columns.length) return [];
      if (entity !== EmployeeColumnEntity.custom_table) {
        return [
          {
            id: entity,
            title: entityTitles[entity],
            columns: columns.map((column) => ({ id: column.id, title: column.title })),
          },
        ];
      }

      const columnsByTable = new Map<number, ApiEmployeeColumn[]>();
      columns.forEach((column) => {
        const tableId = parseCustomTableKey(column.id)?.tableId;
        if (tableId === undefined) return;
        const tableColumns = columnsByTable.get(tableId) ?? [];
        tableColumns.push(column);
        columnsByTable.set(tableId, tableColumns);
      });

      return Array.from(columnsByTable, ([tableId, tableColumns]) => ({
        id: `${entity}-${tableId}`,
        title: tableTitleById.get(tableId) ?? entityTitles[entity],
        columns: tableColumns.map((column) => ({ id: column.id, title: column.title })),
      }));
    });

    return {
      type: ApiColumnConfigType.employees_columns,
      // Имя закреплено: показывать его в выборе нечего, но fixed-логика
      // `useColumns` ждёт его в конфиге
      categories: [
        {
          id: "fixed",
          title: entityTitles[EmployeeColumnEntity.personal],
          columns: [
            { id: EmployeeColumnId.employee_name, title: t("core:full_name"), isFixed: true },
          ],
        },
        ...categories,
      ],
    };
  }, [columnsByEntity, columnDefinitions, entityTitles, tableTitleById, t]);

  /**
   * Ключи колонок для вьюхи: старые вьюхи хранят legacy-id и slug'и, их надо
   * привести к ключам. Значения, которых нет в каталоге, отбрасываются — колонка
   * могла исчезнуть вместе с правами или кастомным полем.
   */
  const migrateSeedColumns = useCallback(
    (ids: string[]): string[] => {
      return ids
        .map((id) => migrateLegacyColumnId(id))
        .filter((key) => columnByKey.has(key) && Boolean(columnDefinitions[key]));
    },
    [columnByKey, columnDefinitions]
  );

  return {
    catalogColumns,
    columnByKey,
    columnDefinitions,
    columnConfig,
    filterCategories,
    migrateSeedColumns,
    isLoading,
    isError,
  };
};
